import { Component, computed, inject, input, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Popover } from 'primeng/popover';
import { ListboxModule } from 'primeng/listbox';
import { DragDropModule } from 'primeng/dragdrop';
import { KpiService } from '@myarvato/modules/transport/kpis';
import { KpiCardDefinition } from '@myarvato/components/kpi';
import { TranslatePipe } from '@ngx-translate/core';
import { UserTrackingService } from '@myarvato/core/services';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'transport-manage-kpi-bar',
  imports: [FormsModule, Popover, ListboxModule, DragDropModule, TranslatePipe, ButtonModule],
  templateUrl: './manage-kpi-bar.component.html',
  styleUrl: './manage-kpi-bar.component.scss',
})
export class ManageKpiBarComponent {
  public readonly categoryTracker = input.required<string>();

  private readonly kpiService = inject(KpiService);
  private readonly tracker = inject(UserTrackingService);

  public readonly allKpis = computed(() => this.kpiService.getAllKpis());
  public readonly visibleFields = computed(() => this.kpiService.visibleKpiFields());
  public readonly hasKpiData = computed(() => this.kpiService.kpiData().length > 0);
  private readonly order = computed(() => this.kpiService.getKpiOrder());
  protected readonly popover = viewChild.required(Popover);

  /**
   * Filtered and sorted KPIs based on the order from the service.
   * This computed signal ensures that the KPI list is always sorted correctly.
   */
  public readonly filteredKpis = computed(() => {
    const allKpis = this.allKpis();
    const baseOrder = this.order();

    // Sort ALL KPIs by the master order; non-listed (shouldn't happen) go to end
    return [...allKpis].sort((a, b) => {
      const ia = baseOrder.indexOf(a.field);
      const ib = baseOrder.indexOf(b.field);
      return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
    });
  });

  /**
   * Selected KPIs computed from visible fields.
   * Used for binding to the listbox ngModel.
   */
  protected readonly selectedKpis = computed(() => {
    const visible = this.visibleFields();
    return this.allKpis().filter((kpi) => visible.includes(kpi.field));
  });

  private draggedItem: KpiCardDefinition | null = null;

  // Drag & Drop Events
  protected onDragStart(kpi: KpiCardDefinition) {
    this.draggedItem = kpi;
  }

  protected onManageKpiBarClick(event: Event): void {
    this.popover().toggle(event);
    this.tracker.trackEvent(this.categoryTracker(), 'Manage KPI Bar', 'Button Click');
  }

  /**
   * Handles drag-and-drop reordering of KPIs in the list.
   * Updates the order in the KpiService to persist the new order.
   */
  protected onDrop(target: KpiCardDefinition) {
    if (!this.draggedItem || this.draggedItem === target) {
      return;
    }

    this.tracker.trackEvent(this.categoryTracker(), 'Manage KPI Bar Overlay', 'Drag and Drop Reorder');
    const fullOrder = [...this.kpiService.getKpiOrder()];
    const dragIndex = fullOrder.indexOf(this.draggedItem.field);
    const dropIndex = fullOrder.indexOf(target.field);
    if (dragIndex === -1 || dropIndex === -1) {
      return;
    }

    fullOrder.splice(dragIndex, 1);
    fullOrder.splice(dropIndex, 0, this.draggedItem.field);

    // Update the master order only here → checkbox toggles won't reorder
    this.kpiService.setKpiOrder(fullOrder);

    this.draggedItem = null;
  }

  protected onSelectionListChange(selectedKpis: KpiCardDefinition[]) {
    this.tracker.trackEvent(this.categoryTracker() ?? 'Transport Tracking List', 'Manage KPI Bar Overlay - Checkbox clicked');
    const selectedFields = selectedKpis.map((kpi) => kpi.field);
    const orderedFields = this.filteredKpis()
      .filter((kpi) => selectedFields.includes(kpi.field))
      .map((kpi) => kpi.field);

    this.kpiService.setVisibleKpiFields(orderedFields);
  }
}
